import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, ArrowLeft, Calendar, Clock, MapPin, Users, CheckCircle, XCircle, Plus, PlayCircle, FileCheck, Edit } from 'lucide-react';
import { AuditTrailViewer } from '../../components/AuditTrail';
import { useAlert } from '../../context/AlertContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MeetingsDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useAlert();
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [showMinutesModal, setShowMinutesModal] = useState(false);
  const [activeTab, setActiveTab] = useState('details'); // 'details' or 'audit'
  const [actionItem, setActionItem] = useState({
    description: '',
    assigned_to: '',
    assigned_to_name: '',
    due_date: ''
  });
  const [minutes, setMinutes] = useState('');
  const [users, setUsers] = useState([]);

  useEffect(() => {
    fetchMeeting();
    fetchUsers();
  }, [id]);

  const fetchMeeting = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/meetings/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMeeting(response.data);
      setMinutes(response.data.minutes || '');
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load meeting');
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (err) {
      console.error('Failed to fetch users');
    }
  };

  const handleMarkAttendance = async (userId, attended) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/meetings/${id}/mark-attendance`,
        { user_id: userId, attended },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchMeeting();
    } catch (err) {
      showError('Failed to mark attendance');
    }
  };

  const handleAddActionItem = async () => {
    try {
      const token = localStorage.getItem('token');
      const user = users.find(u => u.id === actionItem.assigned_to);
      
      const payload = {
        ...actionItem,
        assigned_to_name: user?.name || '',
        due_date: new Date(actionItem.due_date).toISOString(),
        status: 'Open'
      };

      await axios.post(
        `${API}/meetings/${id}/action-items`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setShowActionModal(false);
      setActionItem({ description: '', assigned_to: '', assigned_to_name: '', due_date: '' });
      fetchMeeting();
      showSuccess('Action item added successfully');
    } catch (err) {
      showError('Failed to add action item');
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/meetings/${id}/update-status`,
        { status: newStatus, minutes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchMeeting();
      setShowMinutesModal(false);
      showSuccess(`Meeting marked as ${newStatus}`);
    } catch (err) {
      showError('Failed to update meeting status');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Scheduled': 'bg-blue-100 text-blue-800',
      'In Progress': 'bg-yellow-100 text-yellow-800',
      'Completed': 'bg-green-100 text-green-800',
      'Cancelled': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error || !meeting) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <Alert variant="destructive">
          <AlertDescription>{error || 'Meeting not found'}</AlertDescription>
        </Alert>
        <Button onClick={() => navigate('/meetings')} className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Meetings
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="outline"
            onClick={() => navigate('/meetings')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Meetings
          </Button>
          
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{meeting.title}</h1>
              <p className="text-gray-600 mt-1">{meeting.meeting_number}</p>
            </div>
            <div className="flex items-center gap-3">
              <span className={`px-4 py-2 rounded-full text-sm font-semibold ${getStatusColor(meeting.status)}`}>
                {meeting.status}
              </span>
              {meeting.is_mandatory && (
                <span className="px-4 py-2 rounded-full text-sm font-semibold bg-red-100 text-red-800">
                  Mandatory
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mb-6 flex gap-3">
          {/* Edit button - only shown for Scheduled and In Progress meetings */}
          {(meeting.status?.toLowerCase() === 'scheduled' || meeting.status?.toLowerCase() === 'in progress') && (
            <Button
              variant="outline"
              onClick={() => navigate(`/meetings/${id}/edit`)}
              className="border-blue-600 text-blue-600 hover:bg-blue-50"
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit Meeting
            </Button>
          )}

          {/* Start Meeting button */}
          {meeting.status?.toLowerCase() === 'scheduled' && (
            <Button
              onClick={() => handleUpdateStatus('In Progress')}
              className="bg-yellow-600 hover:bg-yellow-700"
            >
              <PlayCircle className="w-4 h-4 mr-2" />
              Start Meeting
            </Button>
          )}

          {/* Complete Meeting button */}
          {meeting.status?.toLowerCase() === 'in progress' && (
            <Button
              onClick={() => setShowMinutesModal(true)}
              className="bg-green-600 hover:bg-green-700"
            >
              <FileCheck className="w-4 h-4 mr-2" />
              Complete Meeting
            </Button>
          )}
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('details')}
              className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'details'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Details
            </button>
            <button
              onClick={() => setActiveTab('audit')}
              className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'audit'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Audit History
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'details' ? (
          <>
        {/* Meeting Details */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Meeting Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-gray-500" />
                <div>
                  <p className="text-sm font-semibold text-gray-600">Date</p>
                  <p className="text-gray-900">{new Date(meeting.scheduled_date).toLocaleDateString()}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-gray-500" />
                <div>
                  <p className="text-sm font-semibold text-gray-600">Time</p>
                  <p className="text-gray-900">
                    {new Date(meeting.scheduled_date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} ({meeting.duration} min)
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-gray-500" />
                <div>
                  <p className="text-sm font-semibold text-gray-600">Location</p>
                  <p className="text-gray-900">{meeting.location}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-gray-500" />
                <div>
                  <p className="text-sm font-semibold text-gray-600">Organizer</p>
                  <p className="text-gray-900">{meeting.organizer_name}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Agenda */}
        {meeting.agenda_items && meeting.agenda_items.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Agenda</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {meeting.agenda_items.map((item, index) => (
                  <div key={item.id || `agenda-${index}`} className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900">{index + 1}. {item.topic}</h4>
                        {item.presenter && (
                          <p className="text-sm text-gray-600 mt-1">Presenter: {item.presenter}</p>
                        )}
                      </div>
                      <span className="text-sm text-gray-500">{item.duration} min</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Attendees */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Attendees ({meeting.attendees?.length || 0})</CardTitle>
          </CardHeader>
          <CardContent>
            {meeting.attendees && meeting.attendees.length > 0 ? (
              <div className="space-y-2">
                {meeting.attendees.map((attendee, index) => (
                  <div key={attendee.id || attendee.user_id || `attendee-${index}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      {attendee.attended ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <XCircle className="w-5 h-5 text-gray-400" />
                      )}
                      <div>
                        <p className="font-semibold text-gray-900">{attendee.user_name}</p>
                        <p className="text-sm text-gray-600">{attendee.role}</p>
                      </div>
                    </div>
                    {meeting.status?.toLowerCase() === 'in progress' && (
                      <Button
                        size="sm"
                        onClick={() => handleMarkAttendance(attendee.user_id, !attendee.attended)}
                        className={attendee.attended ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}
                      >
                        {attendee.attended ? 'Mark Absent' : 'Mark Present'}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">No attendees added</p>
            )}
          </CardContent>
        </Card>

        {/* Action Items */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Action Items</CardTitle>
              {meeting.status?.toLowerCase() === 'in progress' && (
                <Button
                  size="sm"
                  onClick={() => setShowActionModal(true)}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Action
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {meeting.action_items && meeting.action_items.length > 0 ? (
              <div className="space-y-3">
                {meeting.action_items.map((action, index) => (
                  <div key={action.id || `action-${index}`} className="p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">{index + 1}. {action.description}</p>
                        <div className="mt-2 text-sm text-gray-600">
                          <p>Assigned to: <span className="font-semibold">{action.assigned_to_name}</span></p>
                          <p>Due: {new Date(action.due_date).toLocaleDateString()}</p>
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        action.status === 'Completed' ? 'bg-green-100 text-green-800' :
                        action.status === 'In Progress' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {action.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">No action items yet</p>
            )}
          </CardContent>
        </Card>

        {/* Meeting Minutes */}
        {meeting.minutes && (
          <Card>
            <CardHeader>
              <CardTitle>Meeting Minutes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="prose max-w-none">
                <p className="text-gray-700 whitespace-pre-wrap">{meeting.minutes}</p>
              </div>
            </CardContent>
          </Card>
        )}
          </>
        ) : (
          /* Audit History Tab */
          <Card>
            <CardContent className="pt-6">
              <AuditTrailViewer
                entityType="meeting"
                entityId={id}
                title="Meeting Audit History"
              />
            </CardContent>
          </Card>
        )}

        {/* Action Item Modal */}
        {showActionModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Add Action Item</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Description <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={actionItem.description}
                      onChange={(e) => setActionItem({ ...actionItem, description: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      rows="3"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Assign To <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={actionItem.assigned_to}
                      onChange={(e) => setActionItem({ ...actionItem, assigned_to: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      required
                    >
                      <option value="">Select user...</option>
                      {users.map(user => (
                        <option key={user.id} value={user.id}>{user.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Due Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={actionItem.due_date}
                      onChange={(e) => setActionItem({ ...actionItem, due_date: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      required
                    />
                  </div>
                </div>
                <div className="flex gap-3 mt-6">
                  <Button
                    variant="outline"
                    onClick={() => setShowActionModal(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleAddActionItem}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                  >
                    Add Action
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Minutes Modal */}
        {showMinutesModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4">
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Complete Meeting</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Meeting Minutes/Summary
                    </label>
                    <textarea
                      value={minutes}
                      onChange={(e) => setMinutes(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      rows="8"
                      placeholder="Enter meeting minutes, key discussions, and decisions made..."
                    />
                  </div>
                </div>
                <div className="flex gap-3 mt-6">
                  <Button
                    variant="outline"
                    onClick={() => setShowMinutesModal(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => handleUpdateStatus('Completed')}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    Complete Meeting
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MeetingsDetail;
